import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Calendar, MapPin, Sparkles, ArrowRight } from "lucide-react";

export default function Events() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    client.get("/showcases").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const microLots = items.filter((s) => s.kind === "micro-lot");
  const events = items.filter((s) => s.kind === "event");

  return (
    <div data-testid="events-page" className="pt-32 pb-24">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 mb-16">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">Events & Micro Lots</div>
        <h1 className="font-serif-display text-5xl md:text-7xl font-light leading-[0.95] max-w-4xl">
          Rare beans.<br />Small rooms.<br /><em className="not-italic text-[#C85A32]">Sharp moments.</em>
        </h1>
        <p className="mt-8 max-w-3xl text-lg text-[#4A3B32] leading-relaxed">
          A rolling calendar of limited-release micro-lots and small-format events —
          cuppings, tastings, bonfires, and the occasional live release from a single block.
        </p>
      </section>

      {loading ? (
        <div className="py-24 text-center font-serif-display text-3xl text-[#7B6F66]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-24 text-center font-serif-display text-3xl text-[#7B6F66]">Nothing scheduled yet — check back soon.</div>
      ) : (
        <>
          {microLots.length > 0 && (
            <ShowcaseBlock title="Special Micro Lots" Icon={Sparkles} items={microLots} testidPrefix="microlot" />
          )}
          {events.length > 0 && (
            <ShowcaseBlock title="Upcoming Events" Icon={Calendar} items={events} testidPrefix="event" />
          )}
        </>
      )}
    </div>
  );
}

function ShowcaseBlock({ title, Icon, items, testidPrefix }) {
  return (
    <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-12">
      <div className="flex items-center gap-3 mb-10 pb-6 border-b border-[#EBE3D5]">
        <Icon size={20} className="text-[#C85A32]" strokeWidth={1.4} />
        <h2 className="font-serif-display text-3xl md:text-4xl font-light text-[#1C3326]">{title}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {items.map((s) => (
          <article key={s.id} data-testid={`${testidPrefix}-${s.slug}`} className="border border-[#EBE3D5] flex flex-col bg-[#FDFBF7]">
            {s.image && (
              <div className="aspect-[16/9] overflow-hidden bg-[#F4EFE6]">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-6 md:p-8 flex-1 flex flex-col">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32]">
                {s.kind === "event" ? "Event" : "Micro Lot"}
              </div>
              <h3 className="font-serif-display text-2xl md:text-3xl font-light text-[#1C3326] mt-2">{s.title}</h3>
              {s.subtitle && <p className="mt-2 text-sm text-[#7B6F66]">{s.subtitle}</p>}
              <div className="mt-4 space-y-2 text-xs uppercase tracking-[0.15em] text-[#7B6F66]">
                {s.date_window && (
                  <div className="flex items-center gap-2"><Calendar size={12} /> {s.date_window}</div>
                )}
                {s.location && (
                  <div className="flex items-center gap-2"><MapPin size={12} /> {s.location}</div>
                )}
              </div>
              {s.description && <p className="mt-4 text-[#4A3B32] leading-relaxed">{s.description}</p>}
              {s.cta_url && (
                <a
                  href={s.cta_url}
                  target={s.cta_url.startsWith("http") ? "_blank" : undefined}
                  rel={s.cta_url.startsWith("http") ? "noreferrer" : undefined}
                  data-testid={`${testidPrefix}-cta-${s.slug}`}
                  className="inline-flex items-center gap-2 mt-6 self-start bg-[#1C3326] hover:bg-[#15261C] text-[#FDFBF7] px-6 py-3 uppercase text-xs tracking-[0.25em] transition-colors"
                >
                  {s.cta_label || "Learn more"} <ArrowRight size={14} />
                </a>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
